/**
 * Page Banner — Animated Scroll Component
 *
 * Implements the GSAP Flip on Scroll animation for the animated page banner
 * variant (pageBannerAnimated.twig).
 *
 * How it works:
 *   - A small image box in the header section holds `data-flip-element="target"`.
 *   - An empty large box in the video section holds `data-flip-element="wrapper"`.
 *   - As the user scrolls from the first wrapper's centre to the last wrapper's
 *     centre, GSAP Flip applies transforms to the target element so it visually
 *     fills the large box — the element never moves in the DOM.
 *
 * Dependencies: gsap, gsap/ScrollTrigger, gsap/Flip
 * Lenis integration is handled globally by smoothScroll (already running).
 */

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { Flip } from "gsap/Flip";

gsap.registerPlugin(ScrollTrigger, Flip);

/**
 * Build (or rebuild) the Flip timeline.
 * Returns the timeline so it can be killed on resize.
 *
 * @returns {gsap.core.Timeline|null}
 */
function buildFlipTimeline() {
  const wrapperElements = document.querySelectorAll(
    "[data-flip-element='wrapper']"
  );
  const targetEl = document.querySelector("[data-flip-element='target']");

  if (!wrapperElements.length || !targetEl) return null;

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: wrapperElements[0],
      start: "center center",
      endTrigger: wrapperElements[wrapperElements.length - 1],
      end: "center center",
      scrub: 0.25,
    },
  });

  wrapperElements.forEach((element, index) => {
    const nextIndex = index + 1;
    if (nextIndex < wrapperElements.length) {
      const nextWrapperEl = wrapperElements[nextIndex];

      // Calculate vertical centre positions relative to the document so the
      // Flip.fit duration is proportional to the actual scroll distance.
      const nextRect = nextWrapperEl.getBoundingClientRect();
      const thisRect = element.getBoundingClientRect();
      const nextDistance =
        nextRect.top + window.pageYOffset + nextWrapperEl.offsetHeight / 2;
      const thisDistance =
        thisRect.top + window.pageYOffset + element.offsetHeight / 2;
      const offset = nextDistance - thisDistance;

      tl.add(
        Flip.fit(targetEl, nextWrapperEl, {
          duration: offset,
          ease: "none",
        })
      );
    }
  });

  return tl;
}

/**
 * Initialise the animated banner if the component is present on the page.
 * Safe to call unconditionally — exits early if the element is not found.
 */
export function initPageBannerAnimated() {
  if (!document.querySelector(".module-pageBannerAnimated")) return;

  let tl = buildFlipTimeline();

  // Rebuild on resize to recalculate positions
  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (tl) {
        tl.kill();
        gsap.set("[data-flip-element='target']", { clearProps: "all" });
      }
      tl = buildFlipTimeline();
      ScrollTrigger.refresh();
    }, 100);
  });
}
